import React, { useState } from "react";
import {
    Drawer,
    List,
    ListItem,
    ListItemIcon,
    Collapse,
    Box,
    Typography,
    Badge,
    Menu,
    MenuItem,
    Divider
} from "@mui/material";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import HomeIcon from "@mui/icons-material/Home";
import InfoIcon from "@mui/icons-material/Info";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import SettingsIcon from "@mui/icons-material/Settings";
import LogoutIcon from "@mui/icons-material/Logout";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ArrowRightIcon from "@mui/icons-material/ArrowRight";
import NotificationsIcon from "@mui/icons-material/Notifications";
import { useTranslation } from "react-i18next";
import LanguageToggle from "./LanguageToggle";
import { getBottomMenuItems, getMainMenuItems } from "../utils/menuItems";
import { useAppContext } from "../context/AppContext";
import { useNotifications } from "../context/NotificationContext";
import { markNotificationAsRead } from "../api/notifications";

const Sidebar = () => {
    const { user } = useAppContext();
    const { notifications, refreshNotifications } = useNotifications();
    const [open, setOpen] = useState(false);
    const [openSubmenus, setOpenSubmenus] = useState({});
    const [anchorEl, setAnchorEl] = useState(null);  // For the Notification Dropdown
    const location = useLocation();
    const currentPath = location.pathname;
    const theme = useTheme();
    const { t } = useTranslation();
    const navigate = useNavigate();

    const drawerWidth = open ? 250 : 60;

    const toggleSubmenu = (label) => {
        setOpenSubmenus((prev) => {
            const isCurrentlyOpen = !!prev[label];
            return isCurrentlyOpen ? {} : { [label]: true };
        });
    };

    const mainItems = getMainMenuItems(t, user);
    const bottomItems = getBottomMenuItems(t, navigate, user);

    const handleSubClick = (sub) => {
        if (sub.action) {
            sub.action();
        }
        setOpen(false);
    };

    const renderItem = ({ icon, label, link, children, action }) => {
        const hasChildren = !!children;

        const handleClick = () => {
            if (action) {
                action();
                return;
            }
            if (hasChildren) {
                toggleSubmenu(label);
            } else {
                setOpenSubmenus({});
                setOpen(false);
            }
        };

        const isActive = currentPath === link;

        return (
            <Box key={label}>
                <ListItem
                    onClick={() => { handleClick() }}
                    component={!hasChildren ? Link : undefined}
                    to={!hasChildren ? link : undefined}
                    sx={{
                        py: 1.5,
                        px: 2,
                        height: 48,
                        display: "flex",
                        alignItems: "center",
                        bgcolor: isActive
                            ? theme.palette.sidebar?.itemBgActive || "transparent"
                            : "transparent",
                        fontWeight: isActive ? "bold" : "normal",
                        transition: "all 0.2s ease-in-out",
                        color: theme.palette.text.primary,
                        '&:hover': {
                            bgcolor: theme.palette.sidebar?.itemBgHover || "rgba(255,255,255,0.08)",
                        },
                    }}
                >
                    <ListItemIcon
                        sx={{
                            color: theme.palette.text.primary,
                            minWidth: 0,
                            mr: 2,
                            justifyContent: "center",
                        }}
                    >
                        {icon}
                    </ListItemIcon>
                    <Box
                        sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            width: "100%",
                            opacity: open ? 1 : 0,
                            visibility: open ? "visible" : "hidden",
                            transition: "opacity 0.3s, visibility 0.3s",
                        }}
                    >
                        <Typography>{label}</Typography>
                        {hasChildren &&
                            (openSubmenus[label] ? (
                                <ExpandLessIcon sx={{ color: theme.palette.text.primary }} />
                            ) : (
                                <ExpandMoreIcon sx={{ color: theme.palette.text.primary }} />
                            ))}
                    </Box>
                </ListItem>

                {hasChildren && (
                    <Collapse in={openSubmenus[label]} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            {children.map((sub) => {
                                const isSubActive = currentPath === sub.link;
                                return (
                                    <ListItem
                                        key={sub.label}
                                        component={!sub.action ? Link : undefined}
                                        to={!sub.action ? sub.link : undefined}
                                        onClick={() => { handleSubClick(sub) }}
                                        sx={{
                                            pl: open ? 7 : 4,
                                            py: 1,
                                            height: 40,
                                            color: theme.palette.text.primary,
                                            bgcolor: isSubActive
                                                ? theme.palette.sidebar?.itemBgActive || "transparent"
                                                : "transparent",
                                            fontWeight: isSubActive ? "bold" : "normal",
                                            display: "flex",
                                            alignItems: "center",
                                            transition: "padding 0.3s",
                                            "&:hover": {
                                                bgcolor:
                                                    theme.palette.sidebar?.itemBgHover || "rgba(255,255,255,0.08)",
                                            },
                                        }}
                                    >
                                        {open && (
                                            <ListItemIcon sx={{ minWidth: 0, mr: 1 }}>
                                                <ArrowRightIcon
                                                    sx={{
                                                        color: theme.palette.text.primary,
                                                        fontSize: 16,
                                                    }}
                                                />
                                            </ListItemIcon>
                                        )}
                                        <Typography
                                            sx={{
                                                fontSize: "0.875rem",
                                                opacity: open ? 1 : 0,
                                                transition: "opacity 0.3s",
                                            }}
                                        >
                                            {sub.label}
                                        </Typography>
                                    </ListItem>
                                );
                            })}
                        </List>
                    </Collapse>
                )}
            </Box>
        );
    };

    // Handle Notification Icon Click
    const handleNotificationClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleNotificationClose = (event) => {
        event.stopPropagation()
        setAnchorEl(null);
    };

    const notificationList = notifications.map((notification, index) => {
        const isRead = notification.is_read;  // Assuming 'read' is a boolean property in the notification object

        // Icon only for 'task_assigned' type
        const notificationIcon = notification.type === 'task_assigned' ? (
            <AssignmentIcon sx={{ color: isRead ? 'gray' : 'blue', marginRight: 2 }} />
        ) : null;  // No icon for other types

        return (
            <MenuItem
                sx={{
                    color: isRead ? 'gray' : 'black',  // Gray for read notifications
                    backgroundColor: isRead ? 'transparent' : 'rgba(0, 0, 0, 0.1)',  // Light background for unread
                    '&:hover': {
                        backgroundColor: isRead ? 'transparent' : 'rgba(0, 0, 0, 0.2)',
                    },
                }}
                key={index}
                onClick={async (event) => {
                    handleNotificationClose(event);
                    await markNotificationAsRead(notification.id);
                    refreshNotifications()
                    if (notification.action_url) {
                        const url = new URL(notification.action_url, window.location.origin);

                        const pathname = url.pathname;
                        const queryParams = new URLSearchParams(url.search);

                        const taskId = queryParams.get('task_id') || notification.task_id;
                        const currentSearchParams = new URLSearchParams(location.search);
                        const currentTaskId = currentSearchParams.get('task_id');

                        if (taskId && currentTaskId !== taskId) {
                            navigate({
                                pathname: pathname,
                                search: `?task_id=${taskId}`,
                            }, { replace: true });
                        }
                    }
                }}
            >
                {notificationIcon}
                <Typography>{t(`notification.${notification.type}`)}</Typography>
            </MenuItem>
        );
    });

    return (
        <Drawer
            variant="permanent"
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
            PaperProps={{
                sx: {
                    width: drawerWidth,
                    transition: "width 0.3s ease-in-out",
                    overflowX: "hidden",
                    bgcolor: theme.palette.sidebar?.background || theme.palette.background.default,
                    color: theme.palette.text.primary,
                    border: "none",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                },
            }}
        >
            <Box sx={{ position: "relative", flexGrow: 1 }}>
                {/* Company name as overlay */}
                {open && user?.company?.name && (
                    <Box
                        sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            height: 48,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: open ? "flex-start" : "center",
                            px: open ? 2 : 0,
                            zIndex: 1,
                            background: theme.palette.sidebar?.background || theme.palette.background.default,
                            borderBottom: "1px solid rgba(255,255,255,0.1)"
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 600,
                                color: 'white',
                                textTransform: "uppercase",
                                fontSize: "0.9rem",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis"
                            }}
                        >
                            {user.company.name}
                        </Typography>
                    </Box>
                )}

                {/* Actual menu content with padding-top to not be overlapped */}
                <Box sx={{ mt: 6 }}>
                    <List>{mainItems.map(renderItem)}</List>
                </Box>
            </Box>
            <Box sx={{ mb: 2 }}>
                {open && (
                    <LanguageToggle />
                )}
                <Box sx={{
                    paddingTop: 1.5,
                    px: 2,
                    height: 48,
                    display: "flex",
                    alignItems: "center",
                    transition: "all 0.2s ease-in-out",
                    color: theme.palette.text.primary,
                    '&:hover': {
                        bgcolor: theme.palette.sidebar?.itemBgHover || "rgba(255,255,255,0.08)",
                    },
                }} onClick={handleNotificationClick} >
                    {
                        notifications.filter(notification => !notification.is_read).length > 0 ? (
                            <Badge
                                badgeContent={`${notifications.filter(notification => !notification.is_read).length}${notifications.filter(notification => !notification.is_read).length >= 20 ? '+' : ''}`}
                                color='error'
                            >
                                <NotificationsIcon sx={{ color: theme.palette.text.primary }} />
                            </Badge>
                        ) : (
                            <NotificationsIcon sx={{ color: theme.palette.text.primary }} />
                        )
                    }
                    <Menu
                        sx={{ maxHeight: '400px' }}
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleNotificationClose}
                    >
                        {notificationList}
                        {notifications.length === 0 && (
                            <MenuItem sx={{ color: 'black' }} disabled>{t('notification.no_new_notification')}</MenuItem>
                        )}
                    </Menu>
                </Box>
                <List>{bottomItems.map(renderItem)}</List>
            </Box>
        </Drawer>
    );
};

export default Sidebar;
